/**
 * EditContactDialog — primary + secondary phone, WhatsApp slot, and
 * email. Two phone slots (migration 0032) cover the Indian-SME case
 * where the office has a landline plus the procurement contact's
 * mobile. Either slot can be the WhatsApp endpoint — operators flip
 * the slot with the inline radio next to each phone row.
 *
 * @see prototype/sales/customer.html `.contact` cell
 * @see packages/db/migrations/0032_customer_secondary_phone.sql
 */
import * as React from "react";
import { MessageCircle } from "lucide-react";
import { Input, cn } from "@flint/ui";
import type { customers as contract } from "@flint/api-contract";
import type { z } from "zod";
import { EditDialog } from "./edit-dialog.tsx";
import { SectionField } from "./section-field.tsx";

type CustomerRow = z.infer<typeof contract.customerRow>;

export interface EditContactDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  customer: CustomerRow;
  pending: boolean;
  serverError: string | null;
  onSave: (patch: {
    contactName?: string;
    phone?: string;
    phoneSecondary?: string;
    whatsappOnSecondary?: boolean;
    email?: string;
  }) => Promise<void>;
}

/**
 * Normalize a free-typed phone into a 10-digit Indian mobile, stripping
 * spaces / hyphens / leading 91 country prefix. Matches the existing
 * handler that lived in the previous single-phone version.
 */
function normalizePhone(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  const trimmed = digits.startsWith("91") && digits.length > 10 ? digits.slice(2) : digits;
  return trimmed.slice(0, 10);
}

function displayPhone(value: string): string {
  return value.length > 5 ? `${value.slice(0, 5)} ${value.slice(5)}` : value;
}

export function EditContactDialog({
  open,
  onOpenChange,
  customer,
  pending,
  serverError,
  onSave,
}: EditContactDialogProps) {
  const [contactName, setContactName] = React.useState(customer.contactName ?? "");
  const [phone, setPhone] = React.useState(customer.phone ?? "");
  const [phoneSecondary, setPhoneSecondary] = React.useState(customer.phoneSecondary ?? "");
  const [waOnSecondary, setWaOnSecondary] = React.useState(customer.whatsappOnSecondary);
  const [email, setEmail] = React.useState(customer.email ?? "");

  React.useEffect(() => {
    if (open) {
      setContactName(customer.contactName ?? "");
      setPhone(customer.phone ?? "");
      setPhoneSecondary(customer.phoneSecondary ?? "");
      setWaOnSecondary(customer.whatsappOnSecondary);
      setEmail(customer.email ?? "");
    }
  }, [open, customer]);

  // If the secondary phone is cleared, force the WhatsApp slot back to
  // primary — leaving `waOnSecondary=true` with an empty secondary would
  // be a UX dead-end (the WA pill would point at nothing).
  React.useEffect(() => {
    if (!phoneSecondary.trim() && waOnSecondary) {
      setWaOnSecondary(false);
    }
  }, [phoneSecondary, waOnSecondary]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const patch: Parameters<EditContactDialogProps["onSave"]>[0] = {};

    if (contactName.trim() !== (customer.contactName ?? "")) {
      patch.contactName = contactName.trim();
    }
    if (phone.trim() !== (customer.phone ?? "")) {
      patch.phone = phone.trim();
    }
    if (phoneSecondary.trim() !== (customer.phoneSecondary ?? "")) {
      patch.phoneSecondary = phoneSecondary.trim();
    }
    if (waOnSecondary !== customer.whatsappOnSecondary) {
      patch.whatsappOnSecondary = waOnSecondary;
    }
    if (email.trim() !== (customer.email ?? "")) {
      patch.email = email.trim();
    }

    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }
    await onSave(patch);
  };

  const hasSecondary = phoneSecondary.trim().length > 0;

  return (
    <EditDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit primary contact"
      description="For invoice + reminder delivery. WhatsApp is preferred; email is the fallback."
      pending={pending}
      serverError={serverError}
      onSubmit={handleSubmit}
      maxWidthClass="max-w-lg"
    >
      <SectionField label="Contact name">
        <Input
          type="text"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          placeholder="e.g. Rajiv Mehta"
          autoFocus
        />
      </SectionField>

      <PhoneRow
        label="Primary phone"
        helper="Office or main mobile."
        value={phone}
        onChange={setPhone}
        isWhatsapp={!waOnSecondary && phone.trim().length > 0}
        onMarkWhatsapp={() => setWaOnSecondary(false)}
        whatsappRadioName="contact-whatsapp-slot"
        canMarkWhatsapp={phone.trim().length > 0}
      />

      <PhoneRow
        label="Secondary phone"
        helper="Optional — e.g. partner / procurement-contact mobile."
        value={phoneSecondary}
        onChange={setPhoneSecondary}
        isWhatsapp={waOnSecondary && hasSecondary}
        onMarkWhatsapp={() => setWaOnSecondary(true)}
        whatsappRadioName="contact-whatsapp-slot"
        canMarkWhatsapp={hasSecondary}
      />

      <SectionField label="Email" optional="fallback">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="rajiv@example.in"
          inputMode="email"
          autoComplete="email"
        />
      </SectionField>
    </EditDialog>
  );
}

/**
 * One phone slot — label / +91 prefix / input / "WhatsApp" radio.
 * Both slots share the same radio `name` so they're mutually
 * exclusive (only one slot can be the WhatsApp endpoint). The
 * radio is disabled when the row's value is empty — marking an
 * empty slot as WhatsApp would render a broken pill on the cover.
 */
function PhoneRow({
  label,
  helper,
  value,
  onChange,
  isWhatsapp,
  onMarkWhatsapp,
  whatsappRadioName,
  canMarkWhatsapp,
}: {
  label: string;
  helper: string;
  value: string;
  onChange: (next: string) => void;
  isWhatsapp: boolean;
  onMarkWhatsapp: () => void;
  whatsappRadioName: string;
  canMarkWhatsapp: boolean;
}) {
  return (
    <SectionField label={label} optional="optional" hint={helper}>
      <div className="flex items-stretch gap-2">
        <div
          className={cn(
            "flex flex-1 items-stretch h-10 rounded-sm overflow-hidden bg-paper border border-line",
            "transition-colors hover:border-line-2",
            "focus-within:border-sky-600 focus-within:ring-2 focus-within:ring-sky-100",
          )}
        >
          <span
            aria-hidden
            className="inline-flex items-center px-2.5 text-ink-2 border-r border-line bg-bg font-mono text-sm tabular-nums"
          >
            +91
          </span>
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            value={displayPhone(value)}
            placeholder="98765 43210"
            onChange={(e) => onChange(normalizePhone(e.target.value))}
            className="flex-1 min-w-0 px-3 bg-transparent text-sm text-ink placeholder:text-muted focus:outline-none"
          />
        </div>
        <label
          className={cn(
            "flex items-center gap-1.5 px-2.5 h-10 rounded-sm border text-xs font-semibold",
            "transition-colors cursor-pointer select-none",
            isWhatsapp
              ? "border-ok-100 bg-ok-50 text-ok"
              : canMarkWhatsapp
                ? "border-line bg-paper text-ink-2 hover:bg-bg"
                : "border-line bg-bg text-muted opacity-50 cursor-not-allowed",
          )}
          aria-label={`Mark ${label} as WhatsApp endpoint`}
        >
          <input
            type="radio"
            name={whatsappRadioName}
            checked={isWhatsapp}
            disabled={!canMarkWhatsapp}
            onChange={() => {
              if (canMarkWhatsapp) onMarkWhatsapp();
            }}
            className="sr-only"
          />
          <MessageCircle size={12} strokeWidth={2.2} aria-hidden />
          WhatsApp
        </label>
      </div>
    </SectionField>
  );
}
